import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useEffect} from "react";
import {UseFormReturn, useForm, useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {Alert, AlertDescription} from "@/components/ui/alert";
import {Checkbox} from "@/components/ui/checkbox";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import {SheetFooter} from "@/components/ui/sheet";
import FormError from "@/components/ui/form-error";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import {Input} from "@/components/ui/input";
import {InputGroup, InputGroupAddon, InputGroupInput} from "@/components/ui/input-group";
import {HouseholdCombobox} from "@/src/modules/households/components/combobox";
import useHousehold from "@/src/modules/households/hooks/use-household";
import type {Household} from "@/src/modules/households/types";
import {
  FORM_SHEET_FOOTER_CLASS,
  FORM_SHEET_ROOT_FORM_CLASS,
} from "@/src/modules/core/lib/form-sheet-layout";
import {FieldHelpLabel} from "@/src/modules/help/components/field-help";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";

import {cn} from "@/lib/utils";
import {getUserPrimaryHouseholdRef} from "@/src/modules/users/lib/helpers";

import {vehicleFormSchema} from "../schemas/vehicle-form-schema";
import {VEHICLE_FORM_HELP_KEYS} from "../lib/vehicle-help";
import {useVehicleByPlate} from "../hooks/use-vehicle-by-plate";
import {getVehicleHouseholdId} from "../lib/get-vehicle-household-id";
import {
  hasVehicleIdentityField,
  isVehicleIdentityRequiredError,
  VEHICLE_IDENTITY_REQUIRED_MESSAGE,
} from "../lib/vehicle-identity";
import {
  VEHICLE_COLOR_OPTIONS,
  findVehicleColorOption,
  type VehicleColorOption,
} from "../lib/vehicle-color-options";
import {
  US_STATE_OPTIONS,
  coerceStateCode,
  type UsStateOption,
} from "@/src/modules/core/lib/us-state-options";
import type {Vehicle, VehicleFormData, VehicleFormHouseholdConfig} from "../types";
import {VehicleMakeModelFields} from "./vehicle-make-model-fields";

type VehicleFormLayout = "sheet" | "inline";

type VehicleFormProps = {
  vehicle?: Vehicle | null;
  prefill?: {plate?: string; state?: string};
  householdConfig: VehicleFormHouseholdConfig;
  onInit: (form: UseFormReturn<VehicleFormData>) => void;
  onSubmit: (data: VehicleFormData) => void;
  /** `sheet` — scroll body + pinned footer (DAR / property pattern). `inline` — legacy embedded form. */
  layout?: VehicleFormLayout;
  className?: string;
};

/** Household combobox is read-only here (edit display only); changes are never applied. */
const NOOP_HOUSEHOLD_CHANGE = () => {};

export function VehicleForm({
  vehicle,
  prefill,
  householdConfig,
  onInit,
  onSubmit,
  layout = "inline",
  className,
}: VehicleFormProps) {
  const {t} = useTranslation("vehicles");
  const {t: tCommon} = useTranslation("common");
  const form = useForm<VehicleFormData>({
    defaultValues: {
      vehicle: null,
      plate: vehicle?.plate ?? prefill?.plate ?? "",
      secondary_plate: vehicle?.secondary_plate?.trim() ?? "",
      make: vehicle?.make ?? "",
      model: vehicle?.model ?? "",
      color: vehicle?.color ?? "",
      state: coerceStateCode(prefill?.state ?? vehicle?.state),
      vin: vehicle?.vin?.trim() ?? "",
      has_dealers_plate: vehicle?.has_dealers_plate === true,
      year:
        vehicle?.year != null && vehicle.year !== ""
          ? Number(vehicle.year)
          : undefined,
      vehicle_make_id: vehicle?.vehicle_make_id ?? null,
      vehicle_model_id: vehicle?.vehicle_model_id ?? null,
    },
    resolver: zodResolver(vehicleFormSchema),
  });
  const {
    formState: {isSubmitting, errors},
  } = form;
  const duplicatePlateError =
    errors.plate?.message === "There is a vehicle that matches this plate.";
  const showCombinedIdentityError = isVehicleIdentityRequiredError(
    errors.plate?.message,
    errors.vin?.message,
  );
  const values = form.watch();
  const hasAllFields =
    hasVehicleIdentityField(values) &&
    Boolean(values.make?.trim()) &&
    Boolean(values.model?.trim()) &&
    Boolean(values.color?.trim()) &&
    Boolean(values.state?.trim());

  const editHouseholdId =
    householdConfig.mode === "edit" ? getVehicleHouseholdId(vehicle) : null;
  // Residency is backend-managed via `resident_id` (authoritative). The `resident`
  // embed below is used only for household label prefill, not residency truthiness.
  const isResidentVehicle = vehicle?.resident_id != null;
  const {household: householdFromApi, isPending: isHouseholdLoading} = useHousehold(editHouseholdId);

  useEffect(() => {
    if (form) {
      onInit(form);
    }
  }, [form]);

  const mergeHouseholdIntoSubmit = (data: VehicleFormData): VehicleFormData => {
    // Household is only carried when editing an existing vehicle (read-only display of
    // its current link). Create no longer asks for household: residency is established
    // later via permanent-parking permit issuance, and `house_hold_id` on create does
    // not assign residency.
    if (householdConfig.mode === "edit") {
      return {...data, house_hold_id: getVehicleHouseholdId(vehicle)};
    }
    return data;
  };

  const showHouseholdRow =
    householdConfig.mode === "edit" && (Boolean(editHouseholdId) || isResidentVehicle);

  const householdComboboxDisabled = true;

  const householdFilter =
    householdConfig.mode === "edit"
      ? {context_property_id: householdConfig.propertyId}
      : undefined;

  /** List API sends `house_hold: { house_hold_id, name }` without full Household row. */
  const householdFallbackFromVehicle: Household | null =
    householdConfig.mode === "edit" && vehicle?.house_hold?.house_hold_id != null
      ? {
          house_hold_id: vehicle.house_hold.house_hold_id,
          name: vehicle.house_hold.name,
          street: "",
          number: "",
          state: "",
          city: "",
          zipcode: "",
          user_id: vehicle.resident?.user_id ?? 0,
          created_at: "",
          updated_at: "",
        }
      : null;

  /** When full user embed includes pivot memberships. */
  const residentPrimaryHousehold = getUserPrimaryHouseholdRef(vehicle?.resident);
  const householdFallbackFromResident: Household | null =
    householdConfig.mode === "edit" &&
    !vehicle?.house_hold?.house_hold_id &&
    residentPrimaryHousehold
      ? {
          house_hold_id: residentPrimaryHousehold.id,
          name: residentPrimaryHousehold.name,
          street: "",
          number: "",
          state: "",
          city: "",
          zipcode: "",
          user_id: vehicle!.resident!.user_id,
          created_at: "",
          updated_at: "",
        }
      : null;

  const householdValue =
    householdConfig.mode === "edit"
      ? householdFromApi ?? householdFallbackFromVehicle ?? householdFallbackFromResident
      : null;

  const submitButton = (
    <Button
      className="w-full"
      type="submit"
      disabled={isSubmitting || duplicatePlateError || !hasAllFields}
      data-help-target={VEHICLE_FORM_HELP_KEYS.submit}
    >
      {vehicle ? "Update vehicle" : "Submit vehicle"}
      {isSubmitting ? <Loader2 className="ml-2 size-4 animate-spin" /> : null}
    </Button>
  );

  const fields = (
    <>
        {showCombinedIdentityError ? (
          <Alert variant="destructive">
            <AlertDescription>{VEHICLE_IDENTITY_REQUIRED_MESSAGE}</AlertDescription>
          </Alert>
        ) : null}

        {showHouseholdRow && householdFilter && (
          <FormItem>
            <FormLabel required={householdConfig.mode === "edit" && isResidentVehicle}>
              Household
            </FormLabel>
            {householdConfig.mode === "edit" &&
            isHouseholdLoading &&
            editHouseholdId &&
            !householdFallbackFromVehicle ? (
              <div className="text-muted-foreground flex min-h-10 items-center gap-2 text-sm">
                <Loader2 className="size-4 shrink-0 animate-spin" />
                {tCommon("loading")}
              </div>
            ) : householdConfig.mode === "edit" && isResidentVehicle && !householdValue ? (
              <div className="text-muted-foreground min-h-10 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm">
                Household not available for this vehicle.
              </div>
            ) : (
              <FormControl>
                <HouseholdCombobox
                  value={householdValue}
                  onChange={NOOP_HOUSEHOLD_CHANGE}
                  filter={householdFilter}
                  disabled={householdComboboxDisabled}
                  className="w-full"
                />
              </FormControl>
            )}
          </FormItem>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <VehiclePlateInput isEditing={!!vehicle} showFieldError={!showCombinedIdentityError} />
          </div>
          <FormField
            control={form.control}
            name="secondary_plate"
            render={({field, formState}) => (
              <FormItem className="min-w-0">
                <FieldHelpLabel helpKey={VEHICLE_FORM_HELP_KEYS.secondaryPlate} showIcon={false}>
                  <FormLabel>Secondary plate</FormLabel>
                </FieldHelpLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    autoComplete="off"
                    placeholder={t("form.optional_placeholder")}
                    disabled={!values.plate?.trim() || duplicatePlateError}
                  />
                </FormControl>
                {formState.errors.secondary_plate ? (
                  <FormError error={formState.errors.secondary_plate.message} />
                ) : null}
              </FormItem>
            )}
          />
        </div>

        <p className="text-muted-foreground text-xs">
          License plate or VIN (at least one required)
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="vin"
            render={({field, formState}) => (
              <FormItem className="min-w-0">
                <FieldHelpLabel helpKey={VEHICLE_FORM_HELP_KEYS.vin} showIcon={false}>
                  <FormLabel>VIN</FormLabel>
                </FieldHelpLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={t("form.vin_placeholder")}
                    className="font-mono tabular-nums"
                    disabled={duplicatePlateError}
                  />
                </FormControl>
                {!showCombinedIdentityError && formState.errors.vin ? (
                  <FormError error={formState.errors.vin.message} />
                ) : null}
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="has_dealers_plate"
            render={({field}) => (
              <FormItem
                className="flex flex-row items-center gap-2 sm:mt-6"
                data-help-target={VEHICLE_FORM_HELP_KEYS.dealersPlate}
              >
                <FormControl>
                  <Checkbox
                    checked={field.value === true}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    disabled={duplicatePlateError}
                  />
                </FormControl>
                <FormLabel className="font-normal">Dealer or temporary plate</FormLabel>
              </FormItem>
            )}
          />
        </div>

        <VehicleMakeModelFields disabled={duplicatePlateError} />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="state"
            render={({field, formState}) => {
              const code = field.value?.trim().toUpperCase() ?? "";
              const selectedState: UsStateOption | null =
                code ? (US_STATE_OPTIONS.find((o) => o.value === code) ?? null) : null;
              return (
                <FormItem className="min-w-0">
                  <FieldHelpLabel helpKey={VEHICLE_FORM_HELP_KEYS.state} showIcon={false}>
                    <FormLabel required>State</FormLabel>
                  </FieldHelpLabel>
                  <FormControl>
                    <ComboboxGeneric<UsStateOption>
                      className="w-full min-w-0 normal-case"
                      placeholder={t("form.state_placeholder")}
                      emptyMessage="No state matches."
                      items={US_STATE_OPTIONS}
                      value={selectedState}
                      onChange={(opt, details) => {
                        if (details.reason === "none") return;
                        field.onChange(opt?.value ?? "");
                      }}
                      getItemKey={(o) => o.value}
                      getItemLabel={(o) => o.label}
                      isItemEqual={(a, b) => a?.value === b?.value}
                      disabled={duplicatePlateError}
                      preserveCase
                    />
                  </FormControl>
                  {formState.errors.state && <FormError error={formState.errors.state.message} />}
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="color"
            render={({field, formState}) => {
              const trimmed = field.value?.trim() ?? "";
              const fromList = findVehicleColorOption(trimmed);
              const selectedColor: VehicleColorOption | null = trimmed
                ? (fromList ?? {label: trimmed, value: trimmed})
                : null;
              const colorItems =
                fromList || !trimmed
                  ? VEHICLE_COLOR_OPTIONS
                  : [{label: trimmed, value: trimmed}, ...VEHICLE_COLOR_OPTIONS];
              return (
                <FormItem className="min-w-0">
                  <FieldHelpLabel helpKey={VEHICLE_FORM_HELP_KEYS.color} showIcon={false}>
                    <FormLabel required>Color</FormLabel>
                  </FieldHelpLabel>
                  <FormControl>
                    <ComboboxGeneric<VehicleColorOption>
                      className="w-full min-w-0 normal-case"
                      placeholder={t("filters.color")}
                      emptyMessage="No color matches."
                      items={colorItems}
                      value={selectedColor}
                      onChange={(opt, details) => {
                        if (details.reason === "none") return;
                        field.onChange(opt?.value ?? "");
                      }}
                      getItemKey={(o) => o.value}
                      getItemLabel={(o) => o.label}
                      isItemEqual={(a, b) =>
                        (a?.value ?? "").toLowerCase() === (b?.value ?? "").toLowerCase()
                      }
                      disabled={duplicatePlateError}
                      preserveCase
                    />
                  </FormControl>
                  {formState.errors.color && <FormError error={formState.errors.color.message} />}
                </FormItem>
              );
            }}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="year"
            render={({field, formState}) => (
              <FormItem className="min-w-0">
                <FieldHelpLabel helpKey={VEHICLE_FORM_HELP_KEYS.year} showIcon={false}>
                  <FormLabel>Year</FormLabel>
                </FieldHelpLabel>
                <FormControl>
                  <Input
                    name={field.name}
                    ref={field.ref}
                    onBlur={field.onBlur}
                    value={field.value === undefined || field.value === null ? "" : field.value}
                    onChange={(e) => {
                      const raw = e.target.value;
                      field.onChange(raw === "" ? undefined : parseInt(raw, 10));
                    }}
                    type="number"
                    min="1900"
                    step="1"
                    pattern="[0-9]*"
                    disabled={duplicatePlateError}
                  />
                </FormControl>
                {formState.errors.year && <FormError error={formState.errors.year.message} />}
              </FormItem>
            )}
          />
        </div>
    </>
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => onSubmit(mergeHouseholdIntoSubmit(data)))}
        className={
          layout === "sheet"
            ? FORM_SHEET_ROOT_FORM_CLASS
            : cn("flex flex-col gap-4 px-4", className)
        }
      >
        {layout === "sheet" ? (
          <>
            <SheetFormScrollBody className="flex flex-col gap-4 px-4 pt-2 pb-4">
              <fieldset className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0">
                {fields}
              </fieldset>
            </SheetFormScrollBody>
            <SheetFooter className={FORM_SHEET_FOOTER_CLASS}>{submitButton}</SheetFooter>
          </>
        ) : (
          <>
            {fields}
            {submitButton}
          </>
        )}
      </form>
    </Form>
  );
}

function VehiclePlateInput({
  isEditing,
  showFieldError,
}: {
  isEditing: boolean;
  showFieldError: boolean;
}) {
  const form = useFormContext<VehicleFormData>();
  const plate = form.watch("plate");
  const trimmedPlate = plate?.trim() ?? "";
  const plateLookupEnabled = Boolean(trimmedPlate) && trimmedPlate.length > 3 && !isEditing;
  const {vehicle: vehicleByPlate, isSearching} = useVehicleByPlate(plate ?? "", plateLookupEnabled);

  useEffect(() => {
    if (vehicleByPlate) {
      form.setError("plate", {message: "There is a vehicle that matches this plate."});
      form.setValue("secondary_plate", "");
      form.setValue("make", "");
      form.setValue("model", "");
      form.setValue("vehicle_make_id", null);
      form.setValue("vehicle_model_id", null);
      form.setValue("color", "");
      form.setValue("state", "");
      form.setValue("vin", "");
      form.setValue("has_dealers_plate", false);
      form.setValue("year", undefined);
    } else if (!isSearching) {
      form.clearErrors("plate");
    }
  }, [vehicleByPlate, isSearching]);

  return (
    <div className="min-w-0">
      <FormField
        control={form.control}
        name="plate"
        render={({field, formState}) => (
          <FormItem>
            <FieldHelpLabel helpKey={VEHICLE_FORM_HELP_KEYS.plate} showIcon={false}>
              <FormLabel>License Plate</FormLabel>
            </FieldHelpLabel>
            <FormControl>
              <InputGroup>
                <InputGroupInput {...field} />
                <InputGroupAddon align="inline-end">
                  {isSearching ? <Loader2 className="size-4 animate-spin" /> : null}
                </InputGroupAddon>
              </InputGroup>
            </FormControl>
            {showFieldError && formState.errors.plate ? (
              <FormError error={formState.errors.plate.message} />
            ) : null}
          </FormItem>
        )}
      />
    </div>
  );
}
